import { Injectable, OnDestroy, PendingTasks, PLATFORM_ID, Signal, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { isPlatformBrowser, isPlatformServer, Location } from '@angular/common';

import { Store } from '@ngrx/store';
import { asapScheduler, combineLatest, Observable, of, Subscription, zip, catchError, map, skipWhile } from 'rxjs';

import { LanguageService, TranslateOptions } from '@fcom/ui-translate';
import { finShare, snapshot } from '@fcom/rx';

import { getIn } from '../../utils/data-utils';
import { replaceMsgFormat, ReplaceParams } from '../../utils/replaceMsgFormat';
import { unsubscribe } from '../../utils/unsubscribe';
import * as LanguageActions from '../../actions/language.actions';
import { AppState } from '../../interfaces/app-state.interface';
import { DdsLanguage } from '../../interfaces/dds.interface';
import { currentLocalization, ddsLanguage, language, locale } from '../../selectors/language.selector';
import { CmsMissingKeyError, shouldISendCmsKeyMissingError } from '../../utils/cms-utils';
import { ConfigService } from '../config/config.service';
import { CookieService } from '../cookie/cookie.service';
import { MonitoringLogger } from '../monitoring/monitoring-logger';
import { LocalizationProvider } from './localization.provider';
import { I18nKey, i18nKeyToDataMap, I18nLazyLoadingPrefix } from '../cms/cms-data.service';
import { ddsLangToNewLang } from '../../selectors/dds-languages';

const COOKIE_KEY = 'FinnairComLanguagePreference';

@Injectable()
export class FinLanguageService extends LanguageService implements OnDestroy {
  private store$ = inject<Store<AppState>>(Store);
  private cookieService = inject(CookieService);
  private localizationProvider = inject(LocalizationProvider);
  private configService = inject(ConfigService);
  private monitoringLogger = inject(MonitoringLogger);
  private location = inject(Location);
  private platform = inject(PLATFORM_ID);
  private pendingTasks = inject(PendingTasks);

  private readonly translateOptionsDefaults: Readonly<TranslateOptions> = {
    logErrors: true,
  };

  private lang$: Observable<string>;
  private localizations$: Observable<Record<string, unknown>>;
  private locale$: Observable<string>;
  private subscriptions = new Subscription();

  constructor() {
    super();
    this.allowableLangs = Object.keys(this.configService.cfg.allowedLocales);
    this.lang$ = this.languageFromStore().pipe(finShare());
    this.localizations$ = this.store$.pipe(currentLocalization(), finShare()) as Observable<Record<string, unknown>>;
    this.locale$ = this.localeFromStore().pipe(finShare());
    this.updateLocaleBasedOnLanguage(this.lang$);
    this.setInitialLanguageFromCookieOrPath();
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  verifyLangAllowed = (lang: string, throwExpection = true): boolean => {
    const isLangSupported = lang && this.allowableLangs.includes(lang);
    const isDdsLang = this.allowableLangs.includes(ddsLangToNewLang(this.location.path()));
    if (!(isLangSupported || isDdsLang) && throwExpection) {
      throw new Error(`lang ${lang} not allowed. Allowable: ${this.allowableLangs}`);
    }
    return isLangSupported;
  };

  setLang(lang: string): void {
    const languageValue = this.verifyLangAllowed(lang) ? lang : ddsLangToNewLang(this.location.path());
    this.store$.dispatch(
      LanguageActions.setLanguage({
        language: languageValue,
        locale: this.configService.cfg.allowedLocales[languageValue],
      })
    );
    const expires = new Date();
    expires.setTime(expires.getTime() + FinLanguageService.TTL);
    this.cookieService.setItem(COOKIE_KEY, languageValue, expires);
  }

  get countryCode(): Observable<string> {
    return this.locale.pipe(
      map((l) => {
        const [, countryCode] = l.split('_');
        return countryCode || 'INT';
      })
    );
  }

  get cookieValue(): string {
    return this.cookieService.getItem(COOKIE_KEY) || FinLanguageService.FALLBACK_LANGUAGE;
  }

  // Example: ca-fr
  get lang(): Observable<string> {
    return this.lang$;
  }

  // Example: en or fr
  get langKey(): Observable<string> {
    return this.lang$.pipe(map((val) => val.slice(-2)));
  }

  // Example: en_GB for gb-en – see ALLOWED_LOCALES
  get locale(): Observable<string> {
    return this.locale$;
  }

  get localeValue(): string {
    return snapshot(this.store$.pipe(locale()));
  }

  get langValue(): string {
    return snapshot(this.store$.pipe(language()));
  }

  get langKeyValue(): string {
    return this.langValue.slice(-2);
  }

  get ddsLocaleValue(): DdsLanguage {
    return snapshot(this.store$.pipe(ddsLanguage()));
  }

  get localizations(): Observable<Record<string, unknown>> {
    return this.localizations$;
  }

  translate<T = string>(key: string | string[], args: ReplaceParams = [], options?: TranslateOptions): Observable<T> {
    options = {
      ...this.translateOptionsDefaults,
      ...options,
    };

    const keyAsArray: string[] = Array.isArray(key) ? key : key.split('.');
    const rootKey = keyAsArray[0];
    if (rootKey === i18nKeyToDataMap.taxCodes.lazyLoadingPrefix) {
      this.localizationProvider.triggerLazyLocalizations(I18nKey.TAX_CODES);
    } else if (rootKey === i18nKeyToDataMap.countryRegions.lazyLoadingPrefix) {
      this.localizationProvider.triggerLazyLocalizations(I18nKey.COUNTRY_REGIONS);
    } else if (rootKey === i18nKeyToDataMap.serviceForms.lazyLoadingPrefix) {
      this.localizationProvider.triggerLazyLocalizations(I18nKey.SERVICE_FORMS);
    } else if (rootKey === i18nKeyToDataMap.corporate.lazyLoadingPrefix) {
      this.localizationProvider.triggerLazyLocalizations(I18nKey.CORPORATE);
    } else if (rootKey === i18nKeyToDataMap.travelReady.lazyLoadingPrefix) {
      this.localizationProvider.triggerLazyLocalizations(I18nKey.TRAVEL_READY);
    }

    return this.localizations$.pipe(
      skipWhile((localizations) => {
        if (!Object.values(I18nLazyLoadingPrefix).includes(rootKey as I18nLazyLoadingPrefix)) {
          return false;
        }
        return !(localizations[rootKey] as { loaded?: boolean } | undefined)?.loaded; // wait till loaded is truthy
      }),
      map((localeString) => {
        const str: string = getIn(localeString, keyAsArray) as string;

        if (str === undefined && shouldISendCmsKeyMissingError(keyAsArray)) {
          throw new CmsMissingKeyError(keyAsArray.join('.'));
        }
        return str;
      }),
      catchError((err: unknown) => {
        if (options.logErrors) {
          // Pass the key as a custom Datadog fingerprint so each distinct missing key is its own
          // Error Tracking issue (with the real key in the title) instead of one masked `*.*.*` issue.
          if (err instanceof CmsMissingKeyError) {
            this.monitoringLogger.error(err.message, { cmsKey: err.cmsKey }, err.cmsKey);
          } else {
            this.monitoringLogger.error((err as Error).message || JSON.stringify(err));
          }
        }

        return of('');
      }),
      map((str) => replaceMsgFormat(str, args) as T)
    );
  }

  translateMultiple<T>(keys: string[], args: ReplaceParams = [], options?: TranslateOptions): Observable<T> {
    const translations: Array<Observable<string>> = keys.map((k) => this.translate(k, args, options));
    return zip(...translations) as Observable<T>;
  }

  translateSignal<T = string>(
    key: string | string[],
    args: ReplaceParams = [],
    options?: TranslateOptions
  ): Signal<T | undefined> {
    return toSignal(this.translate<T>(key, args, options));
  }

  translateMultipleSignal<T = string[]>(
    keys: string[],
    args: ReplaceParams = [],
    options?: TranslateOptions
  ): Signal<T | undefined> {
    return toSignal(this.translateMultiple<T>(keys, args, options));
  }

  private languageFromStore(): Observable<string> {
    return this.store$.pipe(language());
  }

  private localeFromStore(): Observable<string> {
    return this.store$.pipe(locale());
  }

  private setInitialLanguageFromCookieOrPath() {
    if (isPlatformBrowser(this.platform)) {
      const languageValue = this.getInitialLanguageValue();

      this.store$.dispatch(
        LanguageActions.setLanguage({
          language: languageValue,
          locale: this.configService.cfg.allowedLocales[languageValue],
        })
      );
    }
  }

  /**
   * Resolve initial language value either from url or from cookie
   */
  private getInitialLanguageValue(): string {
    const currentLang: string | undefined = this.location.path().split('/')[1];

    if (this.verifyLangAllowed(currentLang, false)) {
      return currentLang;
    } else if (this.verifyLangAllowed(this.cookieValue)) {
      return this.cookieValue;
    }

    return ddsLangToNewLang(this.location.path());
  }

  private updateLocaleBasedOnLanguage(languageValue$: Observable<string>): void {
    this.subscriptions.add(
      combineLatest([this.localizationProvider.getLocalizations(languageValue$), languageValue$]).subscribe(
        ([localeValue, lang]) => {
          const removePendingTask = isPlatformServer(this.platform) ? this.pendingTasks.add() : undefined;
          asapScheduler.schedule(() => {
            this.store$.dispatch(
              LanguageActions.setLocalizations({
                language: lang,
                localization: localeValue,
              })
            );
            removePendingTask?.();
          });
        }
      )
    );
  }
}
